import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { copyText } from '../lib/clipboard.ts'
import { lastSeenText } from '../lib/format.ts'
import type { Family } from '../api/types.ts'
import { useAuth } from '../store/auth.ts'

type Invite = { code: string; role: string; expiresAt: string }

const ROLE_NAMES: Record<string, string> = {
  OWNER: 'владелец',
  ADULT: 'взрослый',
  TEEN: 'подросток',
  CHILD: 'ребёнок',
}

const ROLE_HINTS: Record<string, string> = {
  ADULT: 'Полные права: может приглашать других',
  TEEN: 'Как взрослый, но не приглашает',
  CHILD: 'Ограниченные права',
}

export function FamilyPanel({ onClose }: { onClose: () => void }) {
  const [family, setFamily] = useState<Family | null>(null)
  const [invites, setInvites] = useState<Invite[]>([])
  const [role, setRole] = useState<'ADULT' | 'TEEN' | 'CHILD'>('ADULT')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [joinCode, setJoinCode] = useState('')
  const me = useAuth((s) => s.user)
  const [resetFor, setResetFor] = useState<string | null>(null)
  const [resetCode, setResetCode] = useState('')

  const load = () => {
    Promise.all([
      api<{ family: Family }>('/api/family'),
      api<{ invites: Invite[] }>('/api/family/invites'),
    ])
      .then(([f, i]) => {
        setFamily(f.family)
        setInvites(i.invites)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [])

  const createInvite = async () => {
    setBusy(true)
    setError(null)
    try {
      await api('/api/family/invites', { method: 'POST', body: { role, days: 7 } })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать код')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Приглашение уходит ссылкой, а не голым кодом.
   *
   * Код приходится переписывать руками в нужное поле — а поле это
   * ещё надо найти: на входе по умолчанию предлагают завести свою
   * семью, и человек заводит. Так уже случилось: приглашённая завела
   * рядом вторую семью с тем же названием и осталась в ней одна.
   *
   * По ссылке всё открывается сразу заполненным.
   */
  /**
   * Выдать своему код для нового пароля.
   *
   * Показываем прямо в строке человека и не прячем: код придётся
   * продиктовать, а не переслать — приложение писем не отправляет.
   */
  const makeResetCode = async (userId: string) => {
    setBusy(true)
    setError(null)
    try {
      const made = await api<{ code: string }>(`/api/family/members/${userId}/reset-code`, {
        method: 'POST',
      })
      setResetFor(userId)
      setResetCode(made.code)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не вышло выдать код')
    } finally {
      setBusy(false)
    }
  }

  const inviteLink = (code: string) => `${location.origin}/?invite=${code}`

  /**
   * Переход в другую семью по коду.
   *
   * Спрашиваем прямо, а не намёком: прежняя семья исчезает вместе
   * с содержимым, и узнать об этом после нажатия — плохая шутка.
   */
  const join = async () => {
    if (
      !window.confirm(
        'Перейти в другую семью? Нынешняя будет удалена вместе со всем, что в ней есть.',
      )
    ) {
      return
    }

    setBusy(true)
    setError(null)
    try {
      await api('/api/family/join', {
        method: 'POST',
        body: { code: joinCode.trim(), confirm: true },
      })
      // Проще перезагрузить, чем перечитывать всё подряд: сменилось
      // ровно всё — переписки, списки, календарь
      location.reload()
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Не вышло перейти')
      setBusy(false)
    }
  }

  const copy = async (code: string) => {
    // Буфер обмена может быть закрыт браузером — тогда не делаем вид,
    // что получилось: код и так виден на экране, его можно переписать
    if (await copyText(inviteLink(code))) {
      setCopied(code)
      window.setTimeout(() => setCopied(null), 2000)
    } else {
      setError('Не вышло скопировать. Перепиши код вручную — он на экране.')
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{family?.name ?? 'Семья'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {!family && !error && (
          <div className="center-screen" style={{ height: 120 }}>
            <div className="spinner" />
          </div>
        )}

        {family && (
          <>
            <div className="member-list">
              {family.members.map((member) => (
                <div key={member.id} className="member-row">
                  <Avatar
                    id={member.id}
                    name={member.displayName}
                    url={member.avatarUrl}
                    size={40}
                    online={member.online}
                  />
                  <div className="member-text">
                    <div className="member-name">
                      {member.displayName}
                      <span className="tag">{ROLE_NAMES[member.role] ?? member.role}</span>
                      {/* Писем приложение не отправляет, поэтому забывшему
                          пароль код выдаёт кто-то из своих — вслух, за столом */}
                      {member.id !== me?.id && (
                        <button
                          className="link-btn"
                          onClick={() => void makeResetCode(member.id)}
                          disabled={busy}
                        >
                          забыл пароль
                        </button>
                      )}
                    </div>
                    <div className="member-sub">
                      {resetFor === member.id && resetCode ? (
                        <span className="member-reset">
                          Код для нового пароля: <b>{resetCode}</b> · час
                        </span>
                      ) : (
                        lastSeenText(member.lastSeenAt, member.online)
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="sheet-sub">Пригласить</h3>
            <p className="sheet-text">
              Нажми на код — скопируется ссылка. Открывший её попадёт сразу
              в нужную форму. Код действует неделю и срабатывает один раз.
            </p>

            <div className="invite-row">
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
              >
                <option value="ADULT">Взрослый</option>
                <option value="TEEN">Подросток</option>
                <option value="CHILD">Ребёнок</option>
              </select>
              <button className="btn-primary" onClick={() => void createInvite()} disabled={busy}>
                {busy ? '…' : 'Создать код'}
              </button>
            </div>
            <p className="sheet-note">{ROLE_HINTS[role]}</p>

            {invites.length > 0 && (
              <div className="invite-list">
                {invites.map((invite) => (
                  <button
                    key={invite.code}
                    className="invite-code"
                    onClick={() => void copy(invite.code)}
                    title="Нажми, чтобы скопировать ссылку с приглашением"
                  >
                    <span className="invite-code-text">{invite.code}</span>
                    <span className="invite-code-meta">
                      {copied === invite.code
                        ? 'ссылка скопирована'
                        : ROLE_NAMES[invite.role] ?? invite.role}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Тому, кто в своей семье один, почти наверняка сюда и нужно:
                он завёл её по ошибке, не заметив развилки на входе */}
            {family.members.length === 1 && (
              <>
                <h3 className="sheet-sub">Перейти в другую семью</h3>
                <p className="sheet-text">
                  Если тебя звали, а ты завёл свою — введи код из приглашения.
                  Эта семья исчезнет вместе со всем, что в ней есть.
                </p>

                <div className="invite-row">
                  <input
                    className="input input-code"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="ABCD-EFGH"
                  />
                  <button
                    className="btn-quiet"
                    disabled={joinCode.trim().length < 4 || busy}
                    onClick={() => void join()}
                  >
                    Перейти
                  </button>
                </div>
              </>
            )}

            {error && <div className="sheet-error">{error}</div>}
          </>
        )}

        {!family && error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
